import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteKanbanCategory } from "../actions/deleteKanbanCategories.action";
import { KanbanCategory } from "../interfaces/kanbanCategory.interface";
import { FlowTask } from "../interfaces/flowTask.interface";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { useAppDispatch } from "../../../redux/hooks/redux";
import { fetchBenefits } from "../../../permissions/benefits/redux/benefits.thunks";

export const useDeleteKanbanCategory = (kanbanPageUid: string) => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useGlobalSnackbar();
    const dispatch = useAppDispatch();

    return useMutation({
        mutationFn: (categoryUid: string) => deleteKanbanCategory(categoryUid),

        onMutate: async (categoryUid: string) => {
            // 1. Cancel outgoing fetches to avoid overwrites
            await queryClient.cancelQueries({ queryKey: ["kanban-categories"] });
            await queryClient.cancelQueries({ queryKey: ["todo-tasks"] });

            // 2. Snapshot the current state
            const previousCategories = queryClient.getQueryData<KanbanCategory[]>(["kanban-categories"]);
            const previousTasks = queryClient.getQueryData<FlowTask[]>(["todo-tasks"]);

            // 3. Optimistically update categories (global cache)
            if (previousCategories) {
                queryClient.setQueryData<KanbanCategory[]>(["kanban-categories"],
                    (old = []) => old.filter((c) => c.uid !== categoryUid)
                );
            }

            // 4. Optimistically remove tasks belonging to this category
            if (previousTasks) {
                queryClient.setQueryData<FlowTask[]>(["todo-tasks"],
                    (old = []) => old.filter((t) => t.kanbanCategoryUid !== categoryUid)
                );
            }

            return { previousCategories, previousTasks };
        },

        onError: (_err, _categoryUid, context) => {
            // Rollback on failure
            if (context?.previousCategories) {
                queryClient.setQueryData(["kanban-categories"], context.previousCategories);
            }
            if (context?.previousTasks) {
                queryClient.setQueryData(["todo-tasks"], context.previousTasks);
            }
            showSnackbar("Failed to delete category", "error", 6000, "bottom-left");
        },

        onSuccess: () => {
            dispatch(fetchBenefits());
            showSnackbar("Category deleted", "success", 4000, "bottom-left");
        },

        onSettled: () => {
            // Force refresh of the source of truth
            queryClient.invalidateQueries({ queryKey: ["kanban-categories"] });
            queryClient.invalidateQueries({ queryKey: ["todo-tasks"] });
            queryClient.invalidateQueries({ queryKey: ["kanban-pages"] });
        },
    });
};
